import React, { useState } from 'react';
import { Clock3, MapPin, Navigation, PackageCheck, RefreshCw, Store, UserRound } from 'lucide-react';
import MapView from '../../MapView';
import { getStatusLabel } from '../../../utils/statusLabels';
import {
  foodStatusSteps,
  formatMoney,
  getOrderDate,
  orderStepIndex,
  terminalFoodStatuses
} from './restaurantUtils';
import { RestaurantEmptyState, RestaurantSkeletons } from './RestaurantFeedback';

function formatLastUpdated(value) {
  if (!value) return 'Aún no se ha actualizado';
  return `Última actualización: ${new Intl.DateTimeFormat('es-EC', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(value)}`;
}

function OrderTracking({ order, onClose, onRefresh, lastUpdatedAt }) {
  const [refreshing, setRefreshing] = useState(false);
  const currentIndex = orderStepIndex(order.status);
  const mapsUrl = order.google_maps_url || (
    order.pickup_location?.lat && order.pickup_location?.lng
    && order.dropoff_location?.lat && order.dropoff_location?.lng
      ? `https://www.google.com/maps/dir/?api=1&origin=${order.pickup_location.lat},${order.pickup_location.lng}&destination=${order.dropoff_location.lat},${order.dropoff_location.lng}&travelmode=driving`
      : null
  );
  return (
    <div className="rc-tracking">
      <button type="button" className="rc-detail__back" onClick={onClose}>← Mis pedidos</button>
      <div className="rc-tracking__heading">
        <span className="rc-eyebrow">PEDIDO #{String(order.id || '').slice(0, 8).toUpperCase()}</span>
        <h1>{getStatusLabel(order.status, 'food')}</h1>
        <p>{order.restaurant_name} · {getOrderDate(order)}</p>
        <div className="rc-tracking__refresh">
          <button
            type="button"
            className="rc-button rc-button--secondary"
            disabled={refreshing}
            onClick={async () => {
              setRefreshing(true);
              await onRefresh();
              setRefreshing(false);
            }}
          >
            <RefreshCw aria-hidden="true" />{refreshing ? 'Actualizando…' : 'Actualizar estado'}
          </button>
          <small>{formatLastUpdated(lastUpdatedAt)}</small>
        </div>
      </div>
      <div className="rc-tracking__grid">
        <section className="rc-tracking__map">
          {order.pickup_location?.lat && order.dropoff_location?.lat ? (
            <MapView
              pickup={order.pickup_location}
              dropoff={order.dropoff_location}
              vehiclePosition={order.provider_location}
              vehicleIcon="moto"
              vehicleColor="#22D3EE"
              height="360px"
            />
          ) : (
            <RestaurantEmptyState title="Ruta aún no disponible" message="Mostraremos el mapa cuando el pedido tenga ubicaciones válidas." />
          )}
          {!order.provider_location && !terminalFoodStatuses.has(order.status) && (
            <p className="rc-map-note"><MapPin aria-hidden="true" />Aún no hay una ubicación en vivo del repartidor.</p>
          )}
          {mapsUrl && (
            <a className="rc-button rc-button--secondary" href={mapsUrl} target="_blank" rel="noreferrer">
              <Navigation aria-hidden="true" />Abrir ruta en Google Maps
            </a>
          )}
        </section>
        <aside className="rc-tracking__aside">
          <div className="rc-order-party"><Store aria-hidden="true" /><div><small>Restaurante</small><strong>{order.restaurant_name}</strong></div></div>
          <div className="rc-order-party"><UserRound aria-hidden="true" /><div><small>Repartidor</small><strong>{order.provider_name || 'Aún no asignado'}</strong></div></div>
          <ol className="rc-timeline">
            {foodStatusSteps.map((step, index) => (
              <li key={step.label} className={index <= currentIndex ? 'is-done' : ''}>
                <span>{index < currentIndex ? '✓' : index + 1}</span>
                <div><strong>{step.label}</strong>{index === currentIndex && <small>Estado actual</small>}</div>
              </li>
            ))}
          </ol>
          {['cancelled', 'rejected_by_restaurant'].includes(order.status) && (
            <p className="rc-field-error">Este pedido no continuará: {getStatusLabel(order.status, 'food')}.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

export default function FoodOrdersList({
  orders,
  loading,
  error,
  onRetry,
  selectedOrderId,
  onSelectOrder,
  onRefreshOrder,
  lastUpdatedAt
}) {
  const selectedOrder = orders.find((order) => order.id === selectedOrderId);
  const active = orders.filter((order) => !terminalFoodStatuses.has(order.status));
  const history = orders.filter((order) => terminalFoodStatuses.has(order.status));
  if (selectedOrder) {
    return (
      <OrderTracking
        order={selectedOrder}
        onClose={() => onSelectOrder(null)}
        onRefresh={onRefreshOrder}
        lastUpdatedAt={lastUpdatedAt}
      />
    );
  }
  if (loading) return <RestaurantSkeletons count={4} />;
  if (error) return <RestaurantEmptyState error title="No pudimos cargar tus pedidos" message={error} actionLabel="Reintentar" onAction={onRetry} />;

  return (
    <div className="rc-orders">
      <div className="rc-page-heading">
        <div><span className="rc-eyebrow">TU HISTORIAL PROVI</span><h1>Mis pedidos</h1><p>Consulta pedidos activos y entregas anteriores.</p></div>
        <button type="button" className="rc-button rc-button--secondary" onClick={onRetry}><RefreshCw aria-hidden="true" />Actualizar</button>
      </div>
      {!orders.length ? (
        <RestaurantEmptyState title="Aún no realizaste pedidos" message="Explora los restaurantes disponibles y arma tu primer pedido." />
      ) : (
        <>
          <OrderSection title="Activos" orders={active} onSelect={onSelectOrder} empty="No tienes pedidos activos." />
          <OrderSection title="Historial" orders={history} onSelect={onSelectOrder} empty="Tu historial aparecerá aquí." />
        </>
      )}
    </div>
  );
}

function OrderSection({ title, orders, onSelect, empty }) {
  return (
    <section className="rc-section">
      <div className="rc-section__heading"><h2>{title}</h2><span>{orders.length}</span></div>
      {!orders.length ? <p className="rc-order-empty">{empty}</p> : (
        <div className="rc-orders-grid">
          {orders.map((order) => (
            <button type="button" className="rc-order-card" key={order.id} onClick={() => onSelect(order.id)}>
              <span className="rc-order-card__icon"><PackageCheck aria-hidden="true" /></span>
              <div>
                <strong>{order.restaurant_name}</strong>
                <p>{getStatusLabel(order.status, 'food')}</p>
                <small><Clock3 aria-hidden="true" />{getOrderDate(order)}</small>
              </div>
              <b>{formatMoney(order.total_price || order.total)}</b>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
